import mermaid from 'mermaid';
import {
    ARTICLE_MERMAID_CLASS_NAME,
    ARTICLE_MERMAID_SOURCE_ATTRIBUTE,
    buildArticleMermaidLabel,
    detectArticleMermaidTypeFromSource,
    normalizeArticleMermaidSource,
    readArticleMermaidNodeValue,
    writeArticleMermaidNodeAttributes,
} from './articleMermaidShared';

export const ARTICLE_MERMAID_RUNTIME_WINDOW_KEY = 'ArticleMermaidRuntime';

const ARTICLE_MERMAID_SELECTOR = `.${ARTICLE_MERMAID_CLASS_NAME}[${ARTICLE_MERMAID_SOURCE_ATTRIBUTE}]`;
const ARTICLE_MERMAID_RENDER_STATE_ATTRIBUTE = 'data-mermaid-render-state';
const ARTICLE_MERMAID_RENDERED_STATE = 'rendered';
const ARTICLE_MERMAID_RENDERING_STATE = 'rendering';
const ARTICLE_MERMAID_PENDING_STATE = 'pending';
const ARTICLE_MERMAID_ERROR_STATE = 'error';
const ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE = 'Diagram unavailable.';
const ARTICLE_MERMAID_PUBLIC_ERROR_DESCRIPTION = 'This diagram could not be displayed.';

let articleMermaidRuntimeInitialized = false;
let articleMermaidRenderCount = 0;
let articleMermaidPublicIntersectionObserver = null;
let articleMermaidPublicMutationObserver = null;
let articleMermaidPublicRenderQueue = Promise.resolve();
let articleMermaidPublicRenderTimer = null;

function ensureArticleMermaidRuntimeInitialized() {
    if (articleMermaidRuntimeInitialized) {
        return;
    }

    mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        suppressErrorRendering: true,
    });

    articleMermaidRuntimeInitialized = true;
}

function formatArticleMermaidError(error) {
    if (!error) {
        return 'Unable to render this Mermaid diagram.';
    }

    if (typeof error === 'string') {
        return error;
    }

    if (typeof error.message === 'string' && error.message.trim() !== '') {
        return error.message.trim();
    }

    return 'Unable to render this Mermaid diagram.';
}

function clearArticleMermaidPreview(element) {
    if (element && typeof element.innerHTML === 'string') {
        element.innerHTML = '';
    }
}

function resolveArticleMermaidRoot(root) {
    if (root && (root.nodeType === 1 || root.nodeType === 9 || typeof root.querySelectorAll === 'function')) {
        return root;
    }

    if (typeof document !== 'undefined') {
        return document;
    }

    return null;
}

function matchesArticleMermaidNode(node) {
    return !!(node && node.nodeType === 1 && typeof node.matches === 'function' && node.matches(ARTICLE_MERMAID_SELECTOR));
}

function findArticleMermaidNodes(root) {
    const resolvedRoot = resolveArticleMermaidRoot(root);
    if (!resolvedRoot) {
        return [];
    }

    const nodes = [];

    if (matchesArticleMermaidNode(resolvedRoot)) {
        nodes.push(resolvedRoot);
    }

    if (typeof resolvedRoot.querySelectorAll === 'function') {
        resolvedRoot.querySelectorAll(ARTICLE_MERMAID_SELECTOR).forEach((node) => nodes.push(node));
    }

    return Array.from(new Set(nodes));
}

function setArticleMermaidRenderState(node, state) {
    if (!node || typeof node.setAttribute !== 'function') {
        return;
    }

    if (!state) {
        node.removeAttribute(ARTICLE_MERMAID_RENDER_STATE_ATTRIBUTE);
        return;
    }

    node.setAttribute(ARTICLE_MERMAID_RENDER_STATE_ATTRIBUTE, state);
}

function normalizeArticleMermaidNode(node) {
    if (!node) {
        return {
            source: '',
            type: '',
            label: buildArticleMermaidLabel(''),
        };
    }

    const normalizedValue = writeArticleMermaidNodeAttributes(node, readArticleMermaidNodeValue(node));
    node.setAttribute('aria-label', normalizedValue.label);

    return normalizedValue;
}

function isArticleMermaidNodeVisible(node) {
    if (!node || !node.isConnected) {
        return false;
    }

    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return true;
    }

    let currentNode = node;
    while (currentNode && currentNode.nodeType === 1) {
        const computedStyle = window.getComputedStyle(currentNode);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            return false;
        }

        currentNode = currentNode.parentElement;
    }

    return node.getClientRects().length > 0;
}

function getArticleMermaidRenderState(node) {
    if (!node || typeof node.getAttribute !== 'function') {
        return '';
    }

    return node.getAttribute(ARTICLE_MERMAID_RENDER_STATE_ATTRIBUTE) || '';
}

function unobserveArticleMermaidNode(node) {
    if (articleMermaidPublicIntersectionObserver && node) {
        articleMermaidPublicIntersectionObserver.unobserve(node);
    }
}

function scheduleArticleMermaidPublicRender(root = null) {
    if (typeof window === 'undefined') {
        return;
    }

    if (articleMermaidPublicRenderTimer) {
        return;
    }

    articleMermaidPublicRenderTimer = window.setTimeout(() => {
        articleMermaidPublicRenderTimer = null;

        const runtime = typeof window !== 'undefined' ? window[ARTICLE_MERMAID_RUNTIME_WINDOW_KEY] : null;
        if (runtime && typeof runtime.renderAll === 'function') {
            runtime.renderAll(resolveArticleMermaidRoot(root) || document);
        }
    }, 32);
}

function ensureArticleMermaidPublicIntersectionObserver() {
    if (articleMermaidPublicIntersectionObserver || typeof window === 'undefined' || typeof IntersectionObserver !== 'function') {
        return;
    }

    articleMermaidPublicIntersectionObserver = new IntersectionObserver((entries) => {
        let shouldRender = false;

        entries.forEach((entry) => {
            if (!entry || !entry.target) {
                return;
            }

            if (entry.isIntersecting || isArticleMermaidNodeVisible(entry.target)) {
                unobserveArticleMermaidNode(entry.target);
                shouldRender = true;
            }
        });

        if (shouldRender) {
            scheduleArticleMermaidPublicRender(document);
        }
    }, {
        rootMargin: '160px 0px',
    });
}

function ensureArticleMermaidPublicMutationObserver() {
    if (articleMermaidPublicMutationObserver || typeof window === 'undefined' || typeof MutationObserver !== 'function') {
        return;
    }

    if (!document.body) {
        return;
    }

    articleMermaidPublicMutationObserver = new MutationObserver((mutations) => {
        let shouldRender = false;

        mutations.forEach((mutation) => {
            if (shouldRender) {
                return;
            }

            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((addedNode) => {
                    if (shouldRender || !addedNode || addedNode.nodeType !== 1) {
                        return;
                    }

                    if (matchesArticleMermaidNode(addedNode) || (typeof addedNode.querySelector === 'function' && addedNode.querySelector(ARTICLE_MERMAID_SELECTOR))) {
                        shouldRender = true;
                    }
                });

                return;
            }

            if (mutation.type === 'attributes' && mutation.target && mutation.target.nodeType === 1) {
                if (matchesArticleMermaidNode(mutation.target) || (typeof mutation.target.querySelector === 'function' && mutation.target.querySelector(ARTICLE_MERMAID_SELECTOR)) || (typeof mutation.target.closest === 'function' && mutation.target.closest(ARTICLE_MERMAID_SELECTOR))) {
                    shouldRender = true;
                }
            }
        });

        if (shouldRender) {
            scheduleArticleMermaidPublicRender(document);
        }
    });

    articleMermaidPublicMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'open', 'aria-expanded', 'data-headlessui-state'],
    });
}

function queueArticleMermaidPublicRender(task) {
    const queuedTask = articleMermaidPublicRenderQueue.then(task, task);
    articleMermaidPublicRenderQueue = queuedTask.catch(() => {});
    return queuedTask;
}

function renderArticleMermaidFallback(node, value) {
    if (!node || !node.ownerDocument) {
        return;
    }

    clearArticleMermaidPreview(node);

    const fallback = node.ownerDocument.createElement('div');
    fallback.className = 'article-mermaid-fallback';

    const fallbackTitle = node.ownerDocument.createElement('div');
    fallbackTitle.className = 'article-mermaid-fallback__title';
    fallbackTitle.textContent = ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE;

    const fallbackDescription = node.ownerDocument.createElement('div');
    fallbackDescription.className = 'article-mermaid-fallback__description';
    fallbackDescription.textContent = ARTICLE_MERMAID_PUBLIC_ERROR_DESCRIPTION;

    fallback.appendChild(fallbackTitle);
    fallback.appendChild(fallbackDescription);
    node.appendChild(fallback);
    node.setAttribute('aria-label', `${value.label}. ${ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE}`);
    setArticleMermaidRenderState(node, ARTICLE_MERMAID_ERROR_STATE);
}

function observeArticleMermaidVisibility(node) {
    ensureArticleMermaidPublicIntersectionObserver();

    if (articleMermaidPublicIntersectionObserver && node) {
        articleMermaidPublicIntersectionObserver.observe(node);
    }
}

export function createArticleMermaidRuntime() {
    return {
        detectType(source) {
            const normalizedSource = normalizeArticleMermaidSource(source);
            if (!normalizedSource) {
                return '';
            }

            ensureArticleMermaidRuntimeInitialized();

            try {
                return mermaid.detectType(normalizedSource) || detectArticleMermaidTypeFromSource(normalizedSource);
            }
            catch (error) {
                return detectArticleMermaidTypeFromSource(normalizedSource);
            }
        },

        async validate(source) {
            const normalizedSource = normalizeArticleMermaidSource(source);
            const detectedType = this.detectType(normalizedSource);

            if (!normalizedSource) {
                return {
                    valid: false,
                    type: detectedType,
                    source: normalizedSource,
                    error: 'Diagram source is required.',
                };
            }

            ensureArticleMermaidRuntimeInitialized();

            try {
                await mermaid.parse(normalizedSource, { suppressErrors: false });

                return {
                    valid: true,
                    type: detectedType,
                    source: normalizedSource,
                    error: '',
                };
            }
            catch (error) {
                return {
                    valid: false,
                    type: detectedType,
                    source: normalizedSource,
                    error: formatArticleMermaidError(error),
                };
            }
        },

        async renderElement(element) {
            if (!matchesArticleMermaidNode(element)) {
                return {
                    rendered: false,
                    valid: false,
                    type: '',
                    source: '',
                    error: 'Mermaid placeholder element is required.',
                };
            }

            const normalizedValue = normalizeArticleMermaidNode(element);
            const existingState = getArticleMermaidRenderState(element);

            if (!normalizedValue.source) {
                renderArticleMermaidFallback(element, normalizedValue);

                return {
                    rendered: false,
                    valid: false,
                    type: normalizedValue.type,
                    source: normalizedValue.source,
                    error: 'Diagram source is required.',
                };
            }

            if (existingState === ARTICLE_MERMAID_RENDERING_STATE) {
                return {
                    rendered: false,
                    pending: true,
                    valid: true,
                    type: normalizedValue.type,
                    source: normalizedValue.source,
                    error: '',
                };
            }

            if (existingState === ARTICLE_MERMAID_RENDERED_STATE && element.querySelector('svg')) {
                return {
                    rendered: true,
                    valid: true,
                    type: normalizedValue.type,
                    source: normalizedValue.source,
                    error: '',
                };
            }

            if (existingState === ARTICLE_MERMAID_ERROR_STATE && element.querySelector('.article-mermaid-fallback')) {
                return {
                    rendered: false,
                    valid: false,
                    type: normalizedValue.type,
                    source: normalizedValue.source,
                    error: ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE,
                };
            }

            if (!isArticleMermaidNodeVisible(element)) {
                setArticleMermaidRenderState(element, ARTICLE_MERMAID_PENDING_STATE);
                observeArticleMermaidVisibility(element);

                return {
                    rendered: false,
                    deferred: true,
                    valid: true,
                    type: normalizedValue.type,
                    source: normalizedValue.source,
                    error: '',
                };
            }

            const validation = await this.validate(normalizedValue.source);

            if (!validation.valid) {
                renderArticleMermaidFallback(element, {
                    ...normalizedValue,
                    type: validation.type || normalizedValue.type,
                });

                return {
                    rendered: false,
                    valid: false,
                    type: validation.type || normalizedValue.type,
                    source: validation.source,
                    error: ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE,
                };
            }

            setArticleMermaidRenderState(element, ARTICLE_MERMAID_RENDERING_STATE);

            return queueArticleMermaidPublicRender(async () => {
                if (!element.isConnected) {
                    return {
                        rendered: false,
                        valid: true,
                        type: validation.type,
                        source: validation.source,
                        error: '',
                    };
                }

                if (!isArticleMermaidNodeVisible(element)) {
                    setArticleMermaidRenderState(element, ARTICLE_MERMAID_PENDING_STATE);
                    observeArticleMermaidVisibility(element);

                    return {
                        rendered: false,
                        deferred: true,
                        valid: true,
                        type: validation.type,
                        source: validation.source,
                        error: '',
                    };
                }

                if (getArticleMermaidRenderState(element) === ARTICLE_MERMAID_RENDERED_STATE && element.querySelector('svg')) {
                    return {
                        rendered: true,
                        valid: true,
                        type: validation.type,
                        source: validation.source,
                        error: '',
                    };
                }

                ensureArticleMermaidRuntimeInitialized();

                try {
                    articleMermaidRenderCount += 1;
                    const renderId = `article-mermaid-public-${articleMermaidRenderCount}`;
                    const { svg, bindFunctions } = await mermaid.render(renderId, validation.source, element);

                    if (!element.isConnected) {
                        return {
                            rendered: false,
                            valid: true,
                            type: validation.type,
                            source: validation.source,
                            error: '',
                        };
                    }

                    clearArticleMermaidPreview(element);
                    element.innerHTML = svg;

                    if (typeof bindFunctions === 'function') {
                        bindFunctions(element);
                    }

                    element.setAttribute('aria-label', normalizeArticleMermaidNode(element).label);
                    setArticleMermaidRenderState(element, ARTICLE_MERMAID_RENDERED_STATE);
                    unobserveArticleMermaidNode(element);

                    return {
                        rendered: true,
                        valid: true,
                        type: validation.type,
                        source: validation.source,
                        error: '',
                    };
                }
                catch (error) {
                    renderArticleMermaidFallback(element, {
                        ...normalizedValue,
                        type: validation.type || normalizedValue.type,
                    });

                    return {
                        rendered: false,
                        valid: false,
                        type: validation.type || normalizedValue.type,
                        source: validation.source,
                        error: ARTICLE_MERMAID_PUBLIC_ERROR_MESSAGE,
                    };
                }
            });
        },

        async renderAll(root = null) {
            const nodes = findArticleMermaidNodes(root);
            if (!nodes.length) {
                return [];
            }

            return Promise.all(nodes.map((node) => this.renderElement(node)));
        },

        initPublicRender(root = null) {
            ensureArticleMermaidRuntimeInitialized();
            ensureArticleMermaidPublicIntersectionObserver();
            ensureArticleMermaidPublicMutationObserver();

            return this.renderAll(root);
        },

        async renderPreview(element, source) {
            if (!element) {
                return {
                    valid: false,
                    type: '',
                    source: normalizeArticleMermaidSource(source),
                    error: 'Preview element is required.',
                };
            }

            const validation = await this.validate(source);
            clearArticleMermaidPreview(element);

            if (!validation.valid) {
                return validation;
            }

            ensureArticleMermaidRuntimeInitialized();

            try {
                articleMermaidRenderCount += 1;
                const renderId = `article-mermaid-preview-${articleMermaidRenderCount}`;
                const { svg, bindFunctions } = await mermaid.render(renderId, validation.source, element);

                element.innerHTML = svg;

                if (typeof bindFunctions === 'function') {
                    bindFunctions(element);
                }

                return validation;
            }
            catch (error) {
                clearArticleMermaidPreview(element);

                return {
                    valid: false,
                    type: validation.type,
                    source: validation.source,
                    error: formatArticleMermaidError(error),
                };
            }
        },
    };
}

const articleMermaidRuntime = createArticleMermaidRuntime();

if (typeof window !== 'undefined') {
    window[ARTICLE_MERMAID_RUNTIME_WINDOW_KEY] = articleMermaidRuntime;
}

export default articleMermaidRuntime;
